import sheetConstants from "sheets/constants"
import isFeatureEnabled from 'shared/featureflags/helperFunctions'

/* eslint-disable max-len */
// Tooltip Text
export const sheetsTooltips = {
    searchAddColumn: "Use Quorum's Search feature to choose the columns you want to add.",
    fields: "Select one or more fields to add a column for each one.",
    interactions: {
        metrics: "Quorum will compute this for each row based on the interactions you select. " +
        "For example, by choosing 'Last Interaction' as the Metric and selecting all of your Meetings, " +
        "Quorum will add your last meeting to each row.",
        notes: "Use search to filter the Notes that Quorum will add up for each row. " +
               "For example, you could choose to add a date filter to display the count of notes from only the last quarter.",
    },
    assignments: {
        supporters: "Use search to filter the assignments that Quorum will use for calculating cell values in this column. ",
    },
    documentCount: `This column will count the number of documents that mention this search term.`,
    issue: `For legislative and relationship statistics, refine Quorum's algorithm to only
        look at the selected issue. Has no effect on other types of statistics, such as
        demographic information."`,
    legislation: {
        bill: "Create a column showing whether each legislator has sponsored or cosponsored a set of bills.",
        vote: "Create a column showing how each legislator voted, either yea/nay or for/against your organization's stance.",
    },
    session: `"For legislative and relationship statistics, refine Quorum's algorithm to only
        look at the selected session. Has no effect on other types of statistics, such as
        demographic information"`,
    createNewListTooltip: `To create a new list and add a corresponding column
 to your spreadsheet, choose where to add the list to and then click Create New List.`,

    datumHeaderCell: `This sheet displays`,
    datumCellPrefix: `Click to view this`,
    datumCellPostfix: `profile`,
}

// store the help texts in one place
export const helpTexts = {
    add_column: {
        buttonText: `Add Column`,
        disabledText: `Sheet Locked`,
        disabledTooltip: `This sheet is locked, and only the creator can add new columns`,
        tooltip: `Add a new column at the end of the sheet`,
    },
    email_all: {
        buttonText: `Email All`,
        tooltip: `Email all people from this sheet in Outbox`
    },
    settings: {
        buttonText: `Settings`,
        disabledText: `Settings`,
        disabledTooltip: `This sheet is locked, and only the creator can change settings`,
        tooltip: `View this sheet's settings`,
    },
    showStats: {
        buttonText: `Show Stats`,
        tooltip: `View bar displaying columns' aggregate values`,
    },
    hideStats: {
        buttonText: `Hide Stats`,
        tooltip: `Hide the bar displaying columns' aggregate values`,
    },
    advanced_math: `Create advanced math equations based on the columns in your sheet.`,
    statistics: `Each legislative statistic will appear as a separate column.
        By default, legislative statistics are calculated across a legislators' entire career. You can
        choose to limit the statistics to a specific issue area or legislative session.`,
    interactions: `First, choose the type of column you'd like to add to your Sheet - the total number of interactions,
        the total donation value, the number of days since a member of your Organization has interacted with the office,
        etc. Then, you can optionally use the Choose Notes button to use Search to filter the interactions that will show
        up in the column. If you'd like to see the number of days since your team has spoken to each individual on the phone,
        you would choose 'Days Inactive' and then use Search to filter for only phone calls.`,
    documents: `Choose “Number of Documents” to display how many times the officials in your sheet have mentioned a specific
        keyword or phrase in your selection of documents. Choose “Date Official Last Mentioned” to display the date when
         the official was last mentioned by others in your selection of documents. Then, click to filter for a selection of documents.`,
    document_mention_for_bills: `This column shows how many times each Bill has been mentioned in all of Quorum's Documents.`,
    news: `Click the button below to use Search to find a set of new articles. Each row of the sheet will then show the
        number of News Articles matching your filters where that individual may be mentioned.`,
    social_documents: `Click the button below to use Search to find a set of Social Media Documents. Each row of the sheet will then show the
        number of documents matching your filters where that individual is the source.`,
    staffers: `With this column, you can add one staffer per row to your Sheet. To prepopulate the column with staffers of
        a certain type or issue area, choose Title or Issue. Otherwise, choose Custom. Then, decide if you'd like to add
        any extra columns of additional staffer information.`,
    relationships: `This column shows the strength of relationship that each row of your Sheet has with a particular legislator.
        First, choose which legislator you would like to see relationships with, then choose the type of relationship score and
        optionally the subject area and session.`,
    legislation: `This column shows the legislative activity of each legislator in the Sheet. First, choose the type of
        legislative activity, and then choose the type of metric (whether they've sponsored a particular bill, for example).
        Finally, use Search to choose a particular set of bills, votes, or amendments to add to the Sheet.`,
    selectRows: `Use Quorum's Search feature to select a set of rows for your sheet.`,
    projectSheets: `For sheets of projects, the rows are automatically set to be all of your projects, so you don't need to choose the rows.`,
    projectSelect: 'Tag this sheet with Issues',
    validNumber: "Please Enter a valid number",
    teamSelect: `Choose the teams that have access to this sheet. If not Teams are provided, the Sheet is accessible to your entire organization`,
    customFieldFromNote: `First choose the custom notes field you want to see, then choose the selection criteria for choosing a note and the
        possible notes to select from. Your sheet will show you the custom field from the note in your selection that the row's person
        participates in. In case the person participates in multiple notes, the selection criteria is used to determine which to display.`,
}

export const colorFormHelpTexts = {
    colorFormTitle: `Custom Cell Colors in this Column`,
    colorFormGeneralDescription: `Use custom color settings to color certain cells in this column based on conditions that you set so that
        your team can visualize, highlight, emphasize, or differentiate among data and information in sheets. Set conditions in the fields
        on the left and choose the color on the right that you would like to apply to cells that meet these conditions. Click ‘Add Condition’
        to add another condition-color pair.`,
    colorFormNumericDescription: `Input a number in the condition field. Any number in the column that is equal to or greater than this number
        will have the associated color.`,
    colorFormSelectDescription: `Select an option from the dropdown and an associated color.`,
    colorFormStringDescription: `Input exact text (case sensitive) and select associated color.`,
}

// store all of the swal objects in one place
export const swalContents = {
    deleteSheet: {
        title: "Are you sure you want to archive this sheet?",
        text: "You will no longer be able to access this sheet",
        icon: "warning",
        dangerMode: true,
        buttons: {
            cancel: "Cancel",
            confirm: {
                text: "Archive Sheet",
                closeModal: false,
                value: true,
            }
        },
    },
    turnOnBypass: {
        title: "I sure hope you know what you are doing",
        text: "Press Go back to Safety if you have no idea what's going on",
        icon: "warning",
        dangerMode: true,
        buttons: {
            cancel: "Go back to Safety",
            confirm: {
                text: "Do this",
                closeModal: true,
                value: true,
            }
        },
    },
    turnOffBypass: {
        title: "We're Going back to Safety",
        text: "What a relief",
        icon: "success",
    },
    chooseName: {
        icon: "warning",
        text: "Please give your new sheet a name.",
        title: "Oops!",
    },
    choosePassword: {
        icon: "warning",
        text: "You asked to require a password for this sheet, but you didn't choose a password. Please go set a password or make a password not required.",
        title: "Oops!",
    },
    deleteColumn: {
        title: "Are you sure you want to delete this column?",
        text: "This column will be removed from your sheet.",
        icon: "warning",
        dangerMode: true,
        buttons: {
            cancel: "Cancel",
            confirm: {
                text: "Delete Column",
                closeModal: false,
                value: true,
            }
        },
    },
    cannotDuplicate: {
        title: "Cannot Duplicate Sheet",
        text: "You must create a sheet before duplicating it.",
        icon: "error",
    },
    failDownloadSheet: {
        title: "Cannot Download Sheet",
        text: "We're sorry, we were unable to download your sheet",
        icon: "error",
    },
    failLoadResourceTable: {
        title: "Error",
        text: "We're sorry, we were unable to load this table",
        icon: "error",
    },
    successDeleteSheet: {
        title: "Successfully Archived Your Sheet",
        icon: "success",
    },
    successDeleteColumn: {
        title: "Successfully Deleted Your Column",
        icon: "success",
    },
    failDeleteSheet: {
        title: "Error",
        text: "We're sorry, we were unable to archive this sheet",
        icon: "error",
    },
    failDeleteColumn: {
        title: "Error",
        text: "We're sorry, we were unable to delete this column",
        icon: "error",
    },
    somethingWentWrong: {
        icon: "warning",
        title: "Oops!",
        text: "Looks like something went wrong. Please try again or refresh this page.",
    },
    selectRows: {
        icon: "warning",
        title: "Oops!",
        text: "Please select some rows for your sheet.",
    },
    // column form configurations
    noBoard: {
        icon: "warning",
        title: 'No Issue Selected',
        text: 'Please choose an issue to create a new list.',
    },
    createList: {
        title: "Create List",
        text: "Name your new list so Quorum can create it for you.",
        content: {
            element: "input",
            attributes: {
                placeholder: "Your List Name...",
            }
        },
        buttons: {
            cancel: "Cancel",
            confirm: {
                text: "Create",
                closeModal: false,
                value: true,
            }
        },
    },
    listHasNoName: {
        icon: "warning",
        title: "List Has No Name",
        text: "Please give your new list a name"
    },
    failCreateList: {
        title: "Error",
        text: "We're sorry, we were unable to create this list for you.",
        icon: "error",
    },
    lockedSheet: {
        title: "This sheet is locked",
        text: "You do not have permission to change the settings of this sheet.",
        icon: "error",
    },
    successCreateList: {
        title: "Successfully Created your list",
        icon: "success",
    },
    failLoadSheet: {
        title: "Error",
        text: "We're sorry, we were unable to load this sheet.",
        icon: "error",
    },
    failCreateSheet: {
        title: "Error",
        text: "We're sorry, we were unable to create this sheet.",
        icon: "error",
    },
    failSaveSheet: {
        title: "Error",
        text: "We're sorry, we were unable to submit your sheet.",
        icon: "error",
    },
    failSubmitColumnForm: {
        title: "Error",
        text: "We're sorry, we were unable to submit your sheet.",
        icon: "error",
    },
    promptDuplicateSheet: {
        buttons: {
            cancel: "Cancel",
            confirm: {
                text: "Duplicate",
                closeModal: false,
                value: true,
            }
        },
        content: {
            element: "input",
            attributes: {
                placeholder: "Your New Sheet Name",
            }
        },
        title: "Name Your Duplicated Sheet",
        text: "An exact copy of this sheet will be created",
        icon: "info",
    },
    downloadSheetFileTypeOptionsColoring: {
        buttons: {
            excel: {
                text: "Excel",
                value: "xlsx",
            },
            csv: {
                text: "CSV",
                value: "csv",
            },
            word: {
                text: "Word",
                value: "docx",
            },
            cancel: "Cancel",

        },
        icon: "info",
        title: "Confirm Sheet Download",
        text: "Choose whether you would like to download this Sheet as an Excel spreadsheet, CSV, or as a Word document."
    },
    downloadSheetFileTypeOptions: {
        buttons: {
            cancel: "Cancel",
            excel: {
                text: "Excel",
                value: "csv",
            },
            word: {
                text: "Word",
                value: "docx",
            },
        },
        icon: "info",
        title: "Confirm Sheet Download",
        text: "Choose whether you would like to download this Sheet as an Excel spreadsheet or as a Word document.",
    },
    failDuplicateSheet: {
        title: "Failed to Duplicate Sheet",
        text: "Sorry, we were unable to duplicate your sheet",
        icon: "error",
    },
    successDuplicateSheet: {
        title: "Successfully Duplicated Sheet",
        text: "Your sheet was successfully duplicated!",
        icon: "success",
        buttons: {
            cancel: "Stay",
            confirm: {
                text: "Go to New Sheet"
            }
        }
    },
    submitValidationErrors: {
        icon: "warning",
        title: "Oops!",
    },
    downloadCenter: {
        notReadyYetSwal: {
            title: "Just a moment...",
            text: "Your PDF is being processed and will be ready soon. Check your inbox within the next few minutes, or refresh this table for updates.",
            icon: "info",
            buttons: {
                cancel: {
                    text: 'Refresh'
                },
                confirm: {
                    text: 'Ok'
                }
            }
        },
        downloadSwal: {
            title: "Download Again?",
            text: "You can download this PDF directly, or check your inbox for the link!",
            icon: 'info',
            buttons: {
                cancel: true,
                confirm: {
                    text: 'Download',
                }
            }
        }
    }
}

export const additionalOptions = {
    lockSheet: {
        onExplanation: "Only the creator of this sheet may add/remove columns and rows",
        offExplanation: "All users with access to this sheet will be able to edit it",
        onText: "Sheet is locked",
        offText: "Sheet is unlocked",
    }
}

export const legislationVoteOrderToggle = {
    onExplanation: "Vote columns will be ordered by bill",
    offExplanation: "Vote columns will be ordered chronologically",
    onText: "Order Votes by Bill",
    offText: "Order Votes Chronologically"
}

// an object containing strings and information for the
// PublicFormComponent to accept as props
export const publicSheetsConfig = {
    description: {
        onText: "Show Description",
        offText: "Don't Show Description",
        onExplanation: "Show a description text box above your shared sheet",
        offExplanation: "Don't show a description text box above your sheet",
        toggleLabel: "Show Description on Shared Sheet",
        textBoxPlaceholder: "Add a description for your sheet...",
    },
    onExplanation: "Anyone with the link (even people outside your organization) can access this Sheet",
    offExplanation: "Only your team can access this Sheet",
    map: {
        onExplanation: "A map will be shown on the shared sheet page if there are fewer than 2500 rows",
        offExplanation: "No Map will be shown on the shared sheet page",
        onText: "Show Map",
        offText: "Hide Map",
        tooltipText: "Show a map of the region of the Bills or Officials in your sheet",
    },
    reorderColumns: {
        onText: "Allow",
        offText: "Don't Allow",
        onExplanation: "Allow users to reorder columns",
        offExplanation: "Prohibit users from reordering columns",
    },
    organizationDesign: {
        onText: "Use Design",
        offText: "Don't Use Design",
        onExplanation: "Use your organization's default design",
        offExplanation: "Use Quorum's default design",
        tooltipText: "Select one of your organization's designs to change the appearance of the shared sheet",
    },
    requirePassword: {
        onExplanation: name => `Visitors must enter the password below to access this ${name}.`,
        offExplanation: name => `Anyone with the link to this ${name} may access it.`,
        onText: "Password Required",
        offText: "Password Not Required",
        tooltipText: name => `As an additional precaution, you may require visitors to enter a password before they can view this ${name}.`,
    }
}

export const shouldRoundConfig = {
    onText: "Values will be rounded",
    offText: "Values won't be rounded",
    onExplanation: "Values under 1000 will be rounded to the nearest integer.",
    offExplanation: "Values under 1000 won't be rounded to the nearest integer.",
}

export const shouldDisplayAsCurrencyConfig = {
    onText: "Values will display as currency",
    offText: "Values will be displayed normally",
    onExplanation: "Values will be displayed as currency without count bar, e.g. $12345.00.",
    offExplanation: "Values will be displayed normally with count bar and no currency symbol, e.g. 12.3K.",
}

// for sheet form
export const defaultErrorText = "It looks like you're missing something. Make sure you've selected something for all input fields."

// for HeaderCell
export const sortWarningText = `This sheet has more than ${sheetConstants.MAX_SMALL_SHEET_SIZE} rows, which is too many for this column to sort. You can reduce the number of rows in this sheet's settings, or by clicking here.`
export const filterWarningText = `This sheet has more than ${sheetConstants.MAX_SMALL_SHEET_SIZE} rows, which is too many for this column to filter. You can reduce the number of rows in this sheet's settings, or by clicking here.`
export const errorText = "There was an error loading this column. Click here to remove this column."
export const multipleFieldsText = "The values in this column are being computed in real time, so unfortunately you cannot sort or filter by it."
export const defaultSortingIndicatorText = "This sheet is currently sorted by this column. Click to unpin default sorting"

// StatCell errors
export const statCellError = {
    dividyByZero: {
        tooltipText: "Cannot divide by 0",
    }
}

export const EMPTY_STATE_NO_RESULTS = {
    empty_state_title: "No Results",
    empty_state_paragraph: "Try removing any filters or refreshing.",
    icon: "search",
    iconFamily: "fad",
}
